/**
 * `/v1/me` and `/v1/me/settings` (00-contracts §13.2).
 *
 * `me` is the identity tier: it is loaded once for the whole authenticated subtree by the router
 * and read from cache everywhere else, so no component ever refetches it to answer "who am I".
 */
import {
  meResponseSchema,
  userSettingsResponseSchema,
  type Locale,
  type MeResponse,
  type UpdateUserSettingsRequest,
  type UserSettingsResponse,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk } from '@/api/query-keys';
import { setActiveOrganizationId } from '@/auth/active-org';
import { leaveGuestOrganization } from '@/auth/guest-org';
import { applyLocale } from '@/i18n';

export function meQueryOptions() {
  return queryOptions({
    queryKey: qk.me(),
    queryFn: async ({ signal }) => {
      const me = await apiRequest({ path: '/me', schema: meResponseSchema, signal });
      // The one place the stored language is applied. Every path that produces a fresh me
      // — router beforeLoad, a settings write, a session bootstrap — goes through here, so
      // the UI language can never disagree with users.locale.
      await applyLocale(me.user.locale);
      return me;
    },
    ...CACHE.identity,
  });
}

/**
 * `/v1/me` sett fra ET BESTEMT HUS (headeren navngir det), uten å røre identitetscachen og språket.
 * I gjestemodus er `qk.me()` gjesteaktøren; dette er rollen og cellene personen har i hjemmet (P11).
 */
export function meInOrganizationQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: qk.meIn(organizationId),
    queryFn: ({ signal }) =>
      apiRequest({ path: '/me', schema: meResponseSchema, organizationId, signal }),
    ...CACHE.identity,
  });
}

export function useMe(): MeResponse {
  return useSuspenseQuery(meQueryOptions()).data;
}

/**
 * Language is a server-side user setting (principle 8, §13.3). The switch is optimistic because
 * the user is looking straight at the thing that changes: `cancelQueries → snapshot →
 * setQueryData → rollback in onError → invalidate in onSettled`.
 */
export function useUpdateMySettings(): UseMutationResult<
  UserSettingsResponse,
  unknown,
  UpdateUserSettingsRequest,
  { previous: MeResponse | undefined; previousLocale: Locale | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateUserSettingsRequest) =>
      apiRequest({
        path: '/me/settings',
        method: 'PATCH',
        body: input,
        schema: userSettingsResponseSchema,
        sendOrganizationHeader: false,
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: qk.me() });
      const previous = queryClient.getQueryData<MeResponse>(qk.me());
      const previousLocale = previous?.user.locale;
      if (previous) {
        queryClient.setQueryData<MeResponse>(qk.me(), {
          ...previous,
          user: {
            ...previous.user,
            locale: input.locale ?? previous.user.locale,
            timezone: input.timezone ?? previous.user.timezone,
          },
        });
      }
      if (input.locale) await applyLocale(input.locale);
      return { previous, previousLocale };
    },
    onError: async (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(qk.me(), context.previous);
      if (context?.previousLocale) await applyLocale(context.previousLocale);
    },
    // Also a deliberate exception: an optimistic mutation must re-sync on FAILURE as well as on
    // success, and the MutationCache handler that owns `meta.invalidates` only runs onSuccess.
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.me() }),
  });
}

/**
 * Switching organization is explicit — the API never guesses (§12.2). The whole cache is
 * invalidated by prefix rather than selectively, because every org-scoped key starts with
 * `['tvitvi','org',orgId]` and a partial wipe is how stale cross-org data survives.
 */
export function useSetActiveOrganization(): UseMutationResult<MeResponse, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organizationId: string) =>
      apiRequest({
        path: '/me/active-organization',
        method: 'PUT',
        body: { organizationId },
        schema: meResponseSchema,
        sendOrganizationHeader: false,
      }),
    onSuccess: (me) => {
      /* Å velge en organisasjon i velgeren er også å gå ut av gjestemodus (sak 232, T5). */
      leaveGuestOrganization();
      setActiveOrganizationId(me.activeOrganizationId);
      queryClient.setQueryData(qk.me(), me);
    },
    meta: { invalidates: [qk.all] },
  });
}
